import { useChainId, useReadContract } from 'wagmi'
import { referralManagerAbi } from '@/abi/referralManagerAbi'
import { getContractAddress } from '@/config/contracts'
import { isHexAddress } from '@/lib/utils'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useReferralRoster } from '@/hooks/chain/useReferralRoster'

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'
// Mirrors ReferralManager._normalizeCode: 3..32 of [A-Za-z0-9_].
const CODE_RE = /^[A-Za-z0-9_]{3,32}$/
const DEBOUNCE_MS = 400

export type AddressConflict = { code: string }
export type CodeConflict = { owner: `0x${string}` }

// Pre-submit conflict detection for the register-referrer form: an
// already-registered address (`AlreadyRegistered`) or taken code (`CodeTaken`) errors
// inline before the wallet prompt instead of reverting on-chain. Inputs are debounced
// and reads fire only for well-formed values, so typing never thrashes the RPC. A
// conflict is reported only when the debounced value still matches the live input —
// stale verdicts are suppressed while the user keeps typing.
export function useReferralRegistrationPreflight(address: string, code: string) {
    const chainId = useChainId()
    const manager = getContractAddress(chainId, 'referralManager')
    const { referrers } = useReferralRoster()

    const liveAddress = address.trim()
    const liveCode = code.trim()
    const debouncedAddress = useDebouncedValue(liveAddress, DEBOUNCE_MS)
    const debouncedCode = useDebouncedValue(liveCode, DEBOUNCE_MS)
    const addressReady = isHexAddress(debouncedAddress)
    const codeReady = CODE_RE.test(debouncedCode)

    const referrerRecord = useReadContract({
        address: manager,
        abi: referralManagerAbi,
        functionName: 'referrers',
        args: [addressReady ? debouncedAddress : ZERO_ADDRESS],
        chainId,
        query: { enabled: Boolean(manager) && addressReady },
    })
    const codeOwner = useReadContract({
        address: manager,
        abi: referralManagerAbi,
        functionName: 'referrerByCode',
        args: [debouncedCode],
        chainId,
        query: { enabled: Boolean(manager) && codeReady },
    })

    const addressSettled = debouncedAddress === liveAddress
    const codeSettled = debouncedCode === liveCode

    // referrers() tuple: [status, shareBpsOverride, codeHash, accrued, lifetimeEarned,
    // lifetimeClaimed] — status 0 is NotRegistered, anything else means the seat is taken.
    const registeredStatus =
        referrerRecord.data !== undefined && Number(referrerRecord.data[0]) !== 0
    const addressConflict: AddressConflict | null =
        addressReady && addressSettled && registeredStatus
            ? {
                  code:
                      referrers.find(
                          (r) => r.address.toLowerCase() === debouncedAddress.toLowerCase(),
                      )?.code ?? 'an existing code',
              }
            : null

    const owner = codeOwner.data
    const codeConflict: CodeConflict | null =
        codeReady && codeSettled && owner !== undefined && owner !== ZERO_ADDRESS ? { owner } : null

    const isChecking =
        (isHexAddress(liveAddress) && (!addressSettled || referrerRecord.isLoading)) ||
        (CODE_RE.test(liveCode) && (!codeSettled || codeOwner.isLoading))

    return { addressConflict, codeConflict, isChecking }
}
